import { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Box } from '@cad-arc';
import { FrNavItemLink } from './fr-nav-item-link';
import { useGetDisplayedShortcutKeyByCommand } from './use-shortcut-key-by-command';
import { useAllEventNotificationsForEntityTypeForEvent } from '../../event-notifications/use-event-notifications';
import { useUnitAssignment } from '../../unit-assignment/unit-assignment-context';
import { EventNotificationsCounter } from '../../event-panel/tabs/components/event-notifications-counter';
import { useEventLocationNotifications } from '../../common/hooks/use-event-location-notifications';
import { Tooltip } from '../../common/components/tooltip';
import { getCounterNotifications } from '../../event-panel/tabs/components/get-counter-notifications';
import { useEventNotificationDisplayNames } from '../../event-notifications/use-event-notification-display-names';
import { FR_EVENT_ROOT_PATH, ROOT_PATH } from '../../router/constants';

const EventNavItem = ({ eventId }: { eventId?: number }) => {
    const getDisplayedShortcutKeyByCommand = useGetDisplayedShortcutKeyByCommand();
    return (
        <FrNavItemLink
            to={eventId ? `${FR_EVENT_ROOT_PATH}/${eventId}` : ROOT_PATH}
            icon="CADEvent"
            label={getDisplayedShortcutKeyByCommand('CURRENT_ASSIGNMENT')}
            data-testid="event-page-nav-link-nav"
        />
    );
};

export const EventDetailsNavItem = observer(() => {
    const unitAssignment = useUnitAssignment();
    const eventNotificationDisplayNames = useEventNotificationDisplayNames();
    const eventId = unitAssignment?.assignedEvent?.eventId;

    const eventNarrativeNotifications = useAllEventNotificationsForEntityTypeForEvent({
        eventId: eventId ?? -1,
        entityType: 'ADDITIONAL_INFO',
    });

    const { eventLocationNotifications } = useEventLocationNotifications(eventId);

    const allNotifications = useMemo(() => ([
        ...eventNarrativeNotifications,
        ...eventLocationNotifications
    ]), [eventLocationNotifications, eventNarrativeNotifications]);

    return (
        <>
            {allNotifications.length > 0 ? (
                <Tooltip
                    placement="top"
                    overlay={getCounterNotifications(allNotifications)
                        .map((eventNotification) =>
                            eventNotification.notificationPriority === 'HIGH'
                                ? `High Priority ${
                                      eventNotificationDisplayNames[
                                          eventNotification.notificationType
                                      ]
                                  }`
                                : eventNotificationDisplayNames[eventNotification.notificationType]
                        )
                        .join(', ')}
                >
                    <Box style={{ position: 'relative' }}>
                        <EventNotificationsCounter
                            notifications={allNotifications}
                            style={{
                                position: 'absolute',
                                top: '3px',
                                right: '25px',
                            }}
                            showTotalCount
                        />
                        <EventNavItem eventId={eventId} />
                    </Box>
                </Tooltip>
            ) : (
                <EventNavItem eventId={eventId} />
            )}
        </>
    );
});
